/**
 * Hooks for QMS Standards (Phase 1): standard templates, standard_requirements, evidence.
 * Standards = processes; templates have isTemplate true and organizationId null.
 */

import { useState, useEffect, useCallback } from 'react';
import qmsStandardService from '../services/qmsStandardService';

export function useStandardTemplates(filters = {}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await qmsStandardService.getStandardTemplates(filters);
      setTemplates(data);
    } catch (err) {
      console.error('useStandardTemplates:', err);
      setError(err.message);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    load();
  }, [load]);

  return { templates, loading, error, refresh: load };
}

export function useStandardsForOrganization(organizationId, filters = {}) {
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setStandards([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await qmsStandardService.getStandardsForOrganization(organizationId, filters);
      setStandards(data);
    } catch (err) {
      console.error('useStandardsForOrganization:', err);
      setError(err.message);
      setStandards([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, JSON.stringify(filters)]);

  useEffect(() => {
    load();
  }, [load]);

  return { standards, loading, error, refresh: load };
}

export function useImplementedByStandards(requirementIds) {
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!requirementIds?.length) {
      setMap({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    qmsStandardService
      .getImplementedByForRequirements(requirementIds)
      .then((data) => {
        if (!cancelled) setMap(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('useImplementedByStandards:', err);
          setMap({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [requirementIds?.join(',') || '']);

  return { implementedByMap: map, loading };
}

export function useStandardRequirements(standardId) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!standardId) {
      setLinks([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await qmsStandardService.getStandardRequirementsByStandard(standardId);
      setLinks(data);
    } catch (err) {
      console.error('useStandardRequirements:', err);
      setError(err.message);
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [standardId]);

  useEffect(() => {
    load();
  }, [load]);

  return { links, loading, error, refresh: load };
}

export function useEvidenceAttachments(standardRequirementId) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!standardRequirementId) {
      setAttachments([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await qmsStandardService.listEvidence(standardRequirementId);
      setAttachments(data);
    } catch (err) {
      console.error('useEvidenceAttachments:', err);
      setError(err.message);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [standardRequirementId]);

  useEffect(() => {
    load();
  }, [load]);

  return { attachments, loading, error, refresh: load };
}

export function useQmsStandardMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cloneTemplatesToOrganization = async (organizationId, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsStandardService.cloneTemplatesToOrganization(
        organizationId,
        userId,
        userName
      );
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const cloneSelectedTemplatesToOrganization = async (organizationId, templateIds, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsStandardService.cloneSelectedTemplatesToOrganization(
        organizationId,
        templateIds,
        userId,
        userName
      );
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearOrganizationStandards = async (organizationId, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsStandardService.clearOrganizationStandards(
        organizationId,
        userId,
        userName
      );
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createStandardRequirement = async (data, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsStandardService.createStandardRequirement(data, userId, userName);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateStandardRequirement = async (srId, updates, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsStandardService.updateStandardRequirement(srId, updates, userId, userName);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteStandardRequirement = async (srId, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsStandardService.deleteStandardRequirement(srId, userId, userName);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const uploadEvidence = async (standardRequirementId, file, userId, userName, description) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsStandardService.uploadEvidence(
        standardRequirementId,
        file,
        userId,
        userName,
        description
      );
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteEvidence = async (attId, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      return await qmsStandardService.deleteEvidence(attId, userId, userName);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    cloneTemplatesToOrganization,
    cloneSelectedTemplatesToOrganization,
    clearOrganizationStandards,
    createStandardRequirement,
    updateStandardRequirement,
    deleteStandardRequirement,
    uploadEvidence,
    deleteEvidence,
    loading,
    error,
  };
}
